import type { MainCardContributionList, MainCardContributionRecord } from "../api/impactContribution.api";
import type { ImpactCardData } from "./impactCardData";

type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as UnknownRecord) : {};

const asString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  return text || undefined;
};

const asNumber = (value: unknown): number | undefined => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : undefined;
};

const VERIFIED_STATUSES = new Set(["verified", "paid", "completed", "success"]);
const REJECTED_STATUSES = new Set(["rejected", "failed", "refunded", "cancelled"]);

function contributorStatus(record: MainCardContributionRecord): "Committed" | "Pending" | "Verified" | "Rejected" {
  const key = String(record.status ?? "committed").trim().toLowerCase();
  if (VERIFIED_STATUSES.has(key)) return "Verified";
  if (REJECTED_STATUSES.has(key)) return "Rejected";
  if (key === "pending" || key === "awaiting_verification" || key === "awaiting verification") return "Pending";
  return "Committed";
}

function contributorName(record: MainCardContributionRecord): string {
  const user = record.user ?? {};
  const nestedUser = asRecord((record as UnknownRecord).contributor);
  return (
    asString(record.contributor_name) ||
    asString(record.name) ||
    asString(record.username) ||
    asString(user.username) ||
    asString(nestedUser.username) ||
    asString(nestedUser.name) ||
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
    "Supporter"
  );
}

function contributorUserId(record: MainCardContributionRecord): number | undefined {
  const user = record.user ?? {};
  const nestedUser = asRecord((record as UnknownRecord).contributor);
  return (
    asNumber((record as UnknownRecord).user_id) ??
    asNumber(user.id) ??
    asNumber(nestedUser.id)
  );
}

function contributionPurchaseId(record: MainCardContributionRecord): number | undefined {
  const nested = asRecord((record as UnknownRecord).purchase);
  return (
    asNumber(record.purchase_id) ??
    asNumber((record as UnknownRecord).purchaseId) ??
    asNumber(nested.id)
  );
}

function contributorAmount(record: MainCardContributionRecord): number | undefined {
  const nested = asRecord((record as UnknownRecord).contribution);
  return (
    asNumber(record.amount) ??
    asNumber((record as UnknownRecord).contribution_amount) ??
    asNumber(nested.amount)
  );
}

export type MappedImpactContributions = {
  contributors: ImpactCardData["contributors"];
  total: number;
  verifiedTotal: number;
  committedTotal: number;
};

export function mapImpactContributions(payload: unknown): MappedImpactContributions {
  const root = asRecord(payload);
  const data = (root.data ?? payload) as MainCardContributionList | undefined;

  let items: unknown[] = [];
  let total: number | undefined;

  if (Array.isArray(data)) {
    items = data;
    total = asNumber(root.total) ?? data.length;
  } else if (data && typeof data === "object") {
    const nested = data as UnknownRecord;
    const list = nested.items ?? nested.results ?? nested.contributions ?? nested.data;
    items = Array.isArray(list) ? list : [];
    total = asNumber(nested.total ?? nested.count ?? nested.total_count) ?? items.length;
  }

  const contributors = items.flatMap((value, index) => {
    const record = asRecord(value) as MainCardContributionRecord;
    const amount = contributorAmount(record);
    if (amount == null) return [];

    const contributedAt =
      asString(record.contributed_at) ?? asString(record.created_at) ?? new Date().toISOString();
    const verifiedAmount =
      asNumber((record as UnknownRecord).verified_amount) ??
      asNumber((record as UnknownRecord).verifiedAmount);

    return [
      {
        id: record.id ?? index,
        name: contributorName(record),
        amount,
        verifiedAmount,
        status: contributorStatus(record),
        contributedAt,
        userId: contributorUserId(record),
        message: asString(record.message),
        accountType: asString((record as UnknownRecord).account_type) ?? asString((record as UnknownRecord).accountType),
        purchaseId: contributionPurchaseId(record),
      },
    ];
  });

  const committedTotal = contributors.reduce((sum, person) => sum + person.amount, 0);
  const verifiedTotal = contributors
    .filter((person) => person.status === "Verified")
    .reduce((sum, person) => sum + (person.verifiedAmount ?? person.amount), 0);

  return {
    contributors,
    total: total ?? contributors.length,
    verifiedTotal,
    committedTotal,
  };
}

export function applyLiveContributions(
  data: ImpactCardData,
  live: MappedImpactContributions,
  fetched: boolean
): ImpactCardData {
  if (!fetched) return data;

  return {
    ...data,
    contributors: live.contributors,
    contributorCount: live.total,
    verifiedFund: live.verifiedTotal,
    committedFund: live.committedTotal,
    progressPercentage:
      data.targetAmount && data.targetAmount > 0
        ? (live.verifiedTotal / data.targetAmount) * 100
        : data.progressPercentage,
    isDemoData: false,
  };
}
